<script setup lang="ts">
import DashboardContentLayout from '@/components/layouts/dashboardLayout/content/DashboardContentLayout.vue';
import MainViewCard from '@/components/common/MainViewCard.vue';
import GroupedWeeklyChart from '@/components/charts/GroupedWeeklyChart.vue';
import SeriesSelector from '@/components/charts/SeriesSelector.vue';
import RecordsTable from '@/components/records/RecordsTable.vue';
import { Button } from '@/components/ui/button';
import { ColumnVisibilityMenu } from '@/components/ui/data-table';
import colors from '@/plugins/tailwind';
import { useFeedDeliveriesPage } from './useFeedDeliveriesPage';

/*
 * Two tables reading an endpoint each, and a chart reading a third - see `useFeedDeliveriesPage`
 * for why the tables are kept in lock-step rather than each owning its own week selection. The
 * chart's Feed Mix selection is entirely local to it: neither table prop below reads
 * `selectedFeedMixIds`, only `series` (the chart) and `SeriesSelector` (the dropdown) do.
 */
const { approved, feedMixesLabel, isError, original, ranking, refetch, resetSelection, selectWeek, selectedFeedMixIds, selectedWeekStart, series, toggleFeedMix } = useFeedDeliveriesPage();
</script>

<template>
    <DashboardContentLayout class="space-y-8" :heading="$t('views.feed.deliveriesV2.header.heading')" :has-period-picker="true" :has-pig-type-picker="true">
        <div v-if="isError" class="py-14 text-center px-4 text-gray-500 text-lg space-y-4">
            <div>{{ $t('views.pigs.records.errors.couldNotLoadData') }}</div>
            <Button variant="outline" @click="refetch()">{{ $t('views.pigs.records.errors.retry') }}</Button>
        </div>
        <div v-else class="space-y-8">
            <MainViewCard class="py-4 px-2">
                <GroupedWeeklyChart
                    :series="series"
                    :selected-week-start="selectedWeekStart"
                    :colors="colors.chart.feedDeliveries"
                    :title="$t('views.feed.deliveriesV2.chart.heading')"
                    series-id-prefix="feed-mix"
                    @week-selected="selectWeek"
                    @selection-reset="resetSelection"
                >
                    <template #prepend-actions>
                        <SeriesSelector :ranking="ranking" :selected-ids="selectedFeedMixIds" :label="feedMixesLabel" @toggle="toggleFeedMix" />
                    </template>
                </GroupedWeeklyChart>
                <!-- Only once there is something to hide - below the default top-5 cap, every Feed
                     Mix is already in the chart, so the note would have nothing to explain. -->
                <p v-if="ranking.length > 5" class="text-sm text-gray-500 px-2 pt-2">{{ $t('views.feed.deliveriesV2.feedMixes.hiddenNote') }}</p>
            </MainViewCard>
            <MainViewCard class="overflow-hidden">
                <RecordsTable
                    v-model:sorting="approved.sorting"
                    v-model:column-filters="approved.columnFilters"
                    :columns="approved.columns"
                    :rows="approved.visibleRows"
                    :totals-subheader="approved.totalsSubheader"
                    :facet-options="approved.facetOptions"
                    :loading="approved.isFetching"
                    :heading="$t('views.feed.deliveriesV2.approved.heading')"
                    :subtitle="approved.subtitle"
                    @form-activity="approved.refreshAfterFormActivity"
                >
                    <template #actions>
                        <ColumnVisibilityMenu
                            :options="approved.columnToggleOptions"
                            :label="$t('views.feed.deliveriesV2.approved.columnsButton')"
                            @toggle="approved.toggleColumn"
                            @select-all="approved.setAllColumns(true)"
                            @deselect-all="approved.setAllColumns(false)"
                        />
                    </template>
                </RecordsTable>
            </MainViewCard>
            <MainViewCard class="overflow-hidden">
                <RecordsTable
                    v-model:sorting="original.sorting"
                    v-model:column-filters="original.columnFilters"
                    :columns="original.columns"
                    :rows="original.visibleRows"
                    :totals-subheader="original.totalsSubheader"
                    :facet-options="original.facetOptions"
                    :loading="original.isFetching"
                    :heading="$t('views.feed.deliveriesV2.original.heading')"
                    :subtitle="original.subtitle"
                    @form-activity="original.refreshAfterFormActivity"
                />
            </MainViewCard>
        </div>
    </DashboardContentLayout>
</template>
